import React, { lazy, Suspense, useEffect, useState } from "react";
import { BrowserRouter as Router, Routes, Route, useLocation, Navigate, useNavigate } from "react-router-dom";
import { isDespiaIOS } from "@/hooks/use-despia";
import { useRevenueCatLinking } from "@/hooks/use-revenuecat-linking";
import { checkDespiaEntitlement, syncEntitlementToUser } from "@/hooks/use-despia-entitlement-check";
import { saveProfileSnapshot } from "@/lib/profileSnapshot";
import { supabase } from "@/integrations/supabase/client";
import { QueryClient, QueryClientProvider, useQueryClient } from "@tanstack/react-query";
import { AuthProvider, useAuth } from "@/components/AuthProvider";
import { RequireAuth } from "@/components/RequireAuth";
import { Toaster } from "@/components/ui/toaster";

import { PWAUpdateNotifier } from "@/components/PWAUpdateNotifier";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { NetworkStatusIndicator } from "@/components/NetworkStatusIndicator";
import { useCapacitorInit } from "@/hooks/use-capacitor-init";
import { useAppLifecycle } from "@/hooks/use-app-lifecycle";
import { SharedElementProvider } from "@/contexts/SharedElementContext";
import { RoxboxPlayerProvider } from "@/contexts/RoxboxPlayerContext";
import { GlobalMiniPlayer } from "@/components/roxbox/GlobalMiniPlayer";
import { AppReadinessProvider } from "@/contexts/AppReadinessContext";
import { AppLoadingGate } from "@/components/AppLoadingGate";
import { AppFlowProvider } from "@/contexts/AppFlowContext";
import { AppFlowOverlay } from "@/components/AppFlowOverlay";

import { MainLayout } from "@/components/MainLayout";

// Critical path - keep eager loaded
import Index from "@/pages/Index";
import Auth from "@/pages/Auth";
import { initAppleAuth } from "@/lib/apple-auth";

// LetsBegin layout should be eager to avoid background flashes between steps
import LetsBeginLayout from "@/pages/letsbegin/LetsBeginLayout";
import LetsBegin from "@/pages/LetsBegin";
import LetsBeginGo from "@/pages/LetsBeginGo";

// UserFlow layout should be eager to avoid background flashes between steps
import UserFlowLayout from "@/pages/userflow/UserFlowLayout";

// Main tabs - eager loaded for instant switching
import MyBundles from "@/pages/MyBundles";
import Search from "@/pages/Search";

// Eager-loaded for instant navigation
import Roxbox from "@/pages/Roxbox";

// Lazy-loaded pages - reduces initial bundle significantly
const ReaderV2 = lazy(() => import("@/pages/ReaderV2"));
const Checkout = lazy(() => import("@/pages/Checkout"));
const Profile = lazy(() => import("@/pages/Profile"));
const Settings = lazy(() => import("@/pages/Settings"));
const ProfileSelection = lazy(() => import("@/pages/ProfileSelection"));
const AdminDashboard = lazy(() => import("@/pages/AdminDashboard"));

// Auth flow pages - only needed during authentication
const AuthSignup = lazy(() => import("@/pages/AuthSignup"));
const AuthOTPVerification = lazy(() => import("@/pages/AuthOTPVerification"));
const EmailVerification = lazy(() => import("@/pages/EmailVerification"));
const AuthLoading = lazy(() => import("@/pages/AuthLoading"));

// UserFlow pages - eager loaded to avoid background flashes
import UserFlowEntry from "@/pages/userflow/UserFlowEntry";
import UserFlowGo from "@/pages/userflow/UserFlowGo";

// iOS-only payment-first flow
const IOSFlow = lazy(() => import("@/pages/IOSFlow"));

// Onboarding flow
const OnboardingOrchestrator = lazy(() => import("@/pages/onboarding/OnboardingOrchestrator"));

// Utility pages - rarely accessed
const RefundPolicy = lazy(() => import("@/pages/RefundPolicy"));
const TermsOfService = lazy(() => import("@/pages/TermsOfService"));
const PrivacyPolicy = lazy(() => import("@/pages/PrivacyPolicy"));
const ShareReceiver = lazy(() => import("@/pages/ShareReceiver"));
const LoadingPage = lazy(() => import("@/components/LoadingPage").then(m => ({ default: m.LoadingPage })));

// Lazy-loaded global component
const GlobalTierSelectorHost = lazy(() => import("@/components/subscription/GlobalTierSelectorHost"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,
      gcTime: 2 * 60 * 1000,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      retry: 1,
    },
  },
});

// Auth-aware Index component - overlay handles profile selection now
function AuthAwareIndex() {
  const { isLoading, user } = useAuth();
  const navigate = useNavigate();
  const [entitlementChecked, setEntitlementChecked] = useState(false);
  const [checkingEntitlement, setCheckingEntitlement] = useState(false);
  const [hasActiveEntitlement, setHasActiveEntitlement] = useState(false);
  const queryClient = useQueryClient();
  
  // Handle pending RevenueCat account linking after iOS purchase + Apple Sign-In
  useRevenueCatLinking();
  
  // Despia iOS: check device entitlements before deciding to redirect to /iosflow
  useEffect(() => {
    if (isLoading) return;
    if (!isDespiaIOS()) {
      setEntitlementChecked(true);
      return;
    }
    if (entitlementChecked || checkingEntitlement) return;
    
    const onboardingCompleted = localStorage.getItem('onboarding_completed');
    
    // If user is signed in and onboarding completed, no need to check
    if (user && onboardingCompleted) {
      setEntitlementChecked(true);
      return;
    }

    // Check if device has an active subscription via Apple receipt
    setCheckingEntitlement(true);
    
    const runCheck = async () => {
      try {
        const hasEntitlement = await checkDespiaEntitlement();
        
        if (hasEntitlement) {
          console.log('[AuthAwareIndex] Device has active subscription — auto-unlocking');
          setHasActiveEntitlement(true);
          
          // Native iOS pattern: subscription belongs to Apple ID, not app account.
          // Auto-create anonymous session and sync entitlement for instant access.
          if (!user) {
            console.log('[AuthAwareIndex] No user session — creating anonymous session');
            let anonUser = null;
            for (let attempt = 1; attempt <= 3; attempt++) {
              const { data: anonData, error: anonError } = await supabase.auth.signInAnonymously();
              if (!anonError && anonData.user) {
                anonUser = anonData.user;
                break;
              }
              console.error(`[AuthAwareIndex] Anonymous sign-in attempt ${attempt} failed:`, anonError);
              if (attempt < 3) await new Promise(r => setTimeout(r, 1000));
            }
            if (!anonUser) {
              console.error('[AuthAwareIndex] All anonymous sign-in attempts failed');
              setEntitlementChecked(true);
              return;
            }
            const synced = await syncEntitlementToUser(anonUser.id);
            if (synced) {
              localStorage.setItem('onboarding_completed', 'true');
              queryClient.invalidateQueries({ queryKey: ['subscriptionStatus'] });
            }
            // Load existing profile snapshot if available (reinstall case)
            try {
              const { data: profile } = await supabase.from('user_profiles')
                .select('id, name, avatar_url')
                .eq('user_id', anonUser.id)
                .eq('is_primary', true)
                .single();
              if (profile) {
                saveProfileSnapshot({ id: profile.id, name: profile.name, avatar_url: profile.avatar_url, profile_type: 'adult' });
                await supabase.from('user_current_profile').upsert({ user_id: anonUser.id, current_profile_id: profile.id });
                sessionStorage.setItem('profile-selection-shown', 'true');
                localStorage.setItem('has-seen-launch-video', 'true');
              }
            } catch (e) {
              console.warn('[AuthAwareIndex] Profile snapshot load failed:', e);
            }
          } else {
            // User exists — just sync the entitlement
            const synced = await syncEntitlementToUser(user.id);
            if (synced) {
              localStorage.setItem('onboarding_completed', 'true');
              queryClient.invalidateQueries({ queryKey: ['subscriptionStatus'] });
            }
          }
          
          setEntitlementChecked(true);
          return;
        }
      } catch (err) {
        console.warn('[AuthAwareIndex] Entitlement check failed:', err);
      }
      
      setEntitlementChecked(true);
    };
    
    runCheck();
  }, [isLoading, user, navigate, entitlementChecked, checkingEntitlement, queryClient]);
  
  // After entitlement check, handle redirects for users without session
  useEffect(() => {
    if (isLoading || !entitlementChecked) return;
    if (!isDespiaIOS()) return;
    
    const onboardingCompleted = localStorage.getItem('onboarding_completed');
    
    if (!user) {
      if (onboardingCompleted && !hasActiveEntitlement) {
        // Subscription expired — clear onboarding and send back to payment flow
        console.log('[AuthAwareIndex] Despia iOS, no entitlement → clearing onboarding, redirecting to /iosflow');
        localStorage.removeItem('onboarding_completed');
        navigate('/iosflow', { replace: true });
        return;
      }
      
      if (!onboardingCompleted && !hasActiveEntitlement) {
        // Never completed onboarding and no entitlement → send to payment flow
        console.log('[AuthAwareIndex] Despia iOS, no user, no onboarding → /iosflow');
        navigate('/iosflow', { replace: true });
        return;
      }
      
      // hasActiveEntitlement case is handled by the entitlement check effect above
      // (auto anonymous sign-in + sync), so we shouldn't reach here
      return;
    }
    
    // Anonymous user who hasn't completed onboarding → send to IOSFlow
    if ((user as any).is_anonymous && !onboardingCompleted) {
      console.log('[AuthAwareIndex] Despia iOS, anonymous user without onboarding → /iosflow');
      navigate('/iosflow', { replace: true });
    }
  }, [isLoading, user, navigate, entitlementChecked, hasActiveEntitlement]);
  
  // While auth or entitlement check is loading, show branded loading screen
  if (isLoading || !entitlementChecked) {
    return (
      <div className="fixed inset-0 bg-background flex flex-col items-center justify-center gap-4">
        <div className="p-1.5 rounded-3xl bg-white/10 backdrop-blur-xl border border-white/20 shadow-2xl drop-shadow-lg animate-pulse">
          <img 
            src="https://hwjejrsktuenskryvsvl.supabase.co/storage/v1/object/public/applogos/thicker_.png"
            alt="BuxBox"
            className="h-16 w-auto"
          />
        </div>
        <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-primary" />
      </div>
    );
  }
  
  // AppFlowOverlay will show profile selection if needed
  return <Index />;
}

function AppContent() {
  useCapacitorInit();
  useAppLifecycle();
  const location = useLocation();

  // Initialize Apple JS SDK on mount
  useEffect(() => {
    initAppleAuth();
  }, []);

  useEffect(() => {
    if ('serviceWorker' in navigator) {
      const handleMessage = async (event: MessageEvent) => {
        // Reserved for future sync functionality
      };

      navigator.serviceWorker.addEventListener('message', handleMessage);
      return () => {
        navigator.serviceWorker.removeEventListener('message', handleMessage);
      };
    }
  }, []);

  useEffect(() => {
    const preventContextMenu = (e: MouseEvent) => {
      e.preventDefault();
      return false;
    };

    const preventDragStart = (e: DragEvent) => {
      e.preventDefault();
      return false;
    };

    const preventCopy = (e: ClipboardEvent) => {
      e.preventDefault();
      return false;
    };

    document.addEventListener('contextmenu', preventContextMenu);
    document.addEventListener('dragstart', preventDragStart);
    document.addEventListener('copy', preventCopy);
    document.addEventListener('cut', preventCopy);

    return () => {
      document.removeEventListener('contextmenu', preventContextMenu);
      document.removeEventListener('dragstart', preventDragStart);
      document.removeEventListener('copy', preventCopy);
      document.removeEventListener('cut', preventCopy);
    };
  }, []);


  return (
    <>
      
      <PWAUpdateNotifier />
      <NetworkStatusIndicator />
      <div id="main-content">
        <Suspense fallback={null}>
          <Routes>
            {/* Auth routes - no persistent navigation */}
            <Route path="/auth" element={<Auth />} />
            <Route path="/auth/signup" element={<AuthSignup />} />
            <Route path="/auth/verify-otp" element={<AuthOTPVerification />} />
            <Route path="/auth-loading" element={<AuthLoading />} />
            <Route path="/auth/apple/callback" element={<AuthLoading />} />
            
            {/* LetsBegin flow with persistent ShaderGradient background */}
            <Route path="/letsbegin" element={<LetsBeginLayout />}>
              <Route index element={<LetsBegin />} />
              <Route path="go" element={<LetsBeginGo />} />
            </Route>

            {/* Userflow steps with persistent background */}
            <Route path="/userflow" element={<UserFlowLayout />}>
              <Route index element={<UserFlowEntry />} />
              <Route path="go" element={<UserFlowGo />} />
            </Route>

            {/* iOS payment-first onboarding */}
            <Route path="/iosflow" element={<UserFlowLayout />}>
              <Route index element={<IOSFlow />} />
            </Route>

            <Route path="/verify-email" element={<EmailVerification />} />

            {/* Onboarding flow - no persistent navigation */}
            <Route
              path="/onboarding/*"
              element={
                <RequireAuth requireProfileSelection={false}>
                  <OnboardingOrchestrator />
                </RequireAuth>
              }
            />

            {/* Loading page */}
            <Route
              path="/loading"
              element={
                <RequireAuth>
                  <LoadingPage />
                </RequireAuth>
              }
            />

            {/* Backwards compatibility redirects for /launch and /profiles */}
            <Route path="/launch" element={<Navigate to="/" replace />} />
            <Route path="/profiles" element={<Navigate to="/" replace />} />

            {/* Reader - no persistent navigation */}
            <Route
              path="/read/:bookId"
              element={
                <RequireAuth requireProfileSelection={true}>
                  <ReaderV2 />
                </RequireAuth>
              }
            />

            {/* Routes with persistent navigation */}
            <Route element={<MainLayout />}>
              <Route path="/" element={<AuthAwareIndex />} />
              <Route path="/checkout" element={<Checkout />} />
              <Route path="/search" element={<Search />} />
              <Route path="/jukebox" element={<Roxbox />} />
              {/* Redirect old roxbox URL to jukebox */}
              <Route path="/roxbox" element={<Navigate to="/jukebox" replace />} />

              <Route
                path="/my-bundles"
                element={
                  <RequireAuth requireProfileSelection={true}>
                    <MyBundles />
                  </RequireAuth>
                }
              />
              <Route
                path="/admin"
                element={
                  <RequireAuth requireProfileSelection={true}>
                    <AdminDashboard />
                  </RequireAuth>
                }
              />
              <Route
                path="/profile"
                element={
                  <RequireAuth requireProfileSelection={true}>
                    <Profile />
                  </RequireAuth>
                }
              />
              <Route
                path="/settings"
                element={
                  <RequireAuth requireProfileSelection={true}>
                    <Settings />
                  </RequireAuth>
                }
              />
              <Route
                path="/settings/offline-storage"
                element={
                  <RequireAuth requireProfileSelection={true}>
                    <div>Offline Storage - Coming Soon</div>
                  </RequireAuth>
                }
              />
            </Route>

            {/* Utility routes - no persistent navigation */}
            <Route path="/share" element={<ShareReceiver />} />
            <Route path="/refund-policy" element={<RefundPolicy />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/terms-of-service" element={<TermsOfService />} />
          </Routes>
        </Suspense>
      </div>
    </>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <Router>
          <AuthProvider>
            <AppReadinessProvider>
              <AppFlowProvider>
                <SharedElementProvider>
                  <RoxboxPlayerProvider>
                    <AppLoadingGate>
                      <AppContent />
                    </AppLoadingGate>
                    {/* Profile/Launch overlay - renders above content when needed */}
                    <AppFlowOverlay />
                    <GlobalMiniPlayer />
                    <Suspense fallback={null}>
                      <GlobalTierSelectorHost />
                    </Suspense>
                    <Toaster />
                  </RoxboxPlayerProvider>
                </SharedElementProvider>
              </AppFlowProvider>
            </AppReadinessProvider>
          </AuthProvider>
        </Router>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}

export default App;
